import { useEffect, useMemo, useRef } from 'react'
import type { MutableRefObject, ReactNode, RefObject } from 'react'
import * as THREE from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import type { LindeBron } from './lindeKaarten'
import { useObjecten } from '../../scene/store'
import { PROEF } from '../proef'
import { OPNAME } from '../film/opname'

/**
 * Een Matic als studiobeeld van Linde (21 oktober): per frame het beeld van
 * de draaitafel dat past bij de hoek waaruit de camera naar de truck kijkt,
 * op het beeldvlak van de teruggerekende Linde-camera (`lindeKaarten.ts`).
 *
 * Het 3D-model blijft staan, maar onzichtbaar: het schrijft alleen nog diepte
 * ("dieptestand-in"). Daardoor klopt wat voor en achter de truck ligt per
 * pixel, werpt hij zijn schaduw en ziet de AO hem nog. Het beeld zelf ligt
 * vóór het model — elke hoek over zijn kijkstraal naar voren geschoven tot
 * net vóór het dichtste punt van de truck, zodat het op het scherm op
 * dezelfde plek blijft — en schrijft geen diepte. Volgorde: de rest van de
 * hal, dan de stand-in (9), het beeld (10) en als laatste wat de truck draagt
 * (11): een pallet op de vorken ligt zo vóór het beeld, maar achter de
 * stand-in als hij erachter hoort.
 *
 * Sinds 22 oktober ("het moet voelen alsof het echt gefilmd is"):
 *
 * - **De stand-in schrijft alleen diepte waar het beeld dekt.** Het 3D-model
 *   is een benadering; waar het buiten het silhouet van Linde steekt, of
 *   dicht is waar Linde's machine open is (de mast van de K-MATIC), zagen
 *   mist, lichtschachten en AO er een truck waar je de hal zag: een glazen
 *   zuil boven de K-MATIC, een waas langs de randen. Nu kijkt elk fragment
 *   van de stand-in langs zijn kijkstraal in het beeld, en schrijft alleen
 *   als het daar dekt.
 * - **Het beeld wordt belicht door de hal.** Linde's render is in hun studio
 *   belicht: egaal en wit, en in een donker gangpad leest dat als een
 *   sticker. Nu is het een materiaal zoals de rest van de hal — het licht van
 *   de armaturen en de omgeving, de kleur, en de schaduw van de stellingen en
 *   de daklichten die over de truck valt. De vorm komt nog uit het beeld zelf.
 * - **Alleen in het beeld van de camera.** De spiegeling in de vloer tekende
 *   het vlak voor de gespiegelde camera, en dat gaf een uitgerekte rode veeg.
 *   Nu staat het daar niet; de stand-in maakt er een donkere plek, als een
 *   contactschaduw.
 *
 * Wat de truck draagt, staat in het model onder een groep met
 * `userData.lading`; die houdt zijn materialen.
 *
 * Beelden worden pas geladen als ze nodig zijn, met een kleine voorraad per
 * truck: een frame van de K-MATIC is 928 × 1280 px, 4,7 MB op de GPU.
 */

const STAP = (2 * Math.PI) / 36
const Y = new THREE.Vector3(0, 1, 0)
/** Zoveel beelden per truck tegelijk op de GPU. */
const VOORRAAD = 6
/** Marge van het beeld vóór het dichtste punt van de truck, in meters. */
const MARGE = 0.05
/**
 * Hoeveel hoger dan de camera van Linde de camera mag kijken voor het beeld
 * plat gaat ogen: daartussen vloeit de film over naar het 3D-model.
 */
const BOVEN = { van: THREE.MathUtils.degToRad(14), tot: THREE.MathUtils.degToRad(24) }

/**
 * De dieptestand-in van één truck: schrijft alleen diepte, en alleen waar
 * het beeld van Linde dekt. Elk fragment volgt zijn kijkstraal tot het vlak
 * van het beeld (de vier hoeken, in wereldcoördinaten), zoekt daar de
 * textuurcoördinaat op, en valt weg als het beeld daar doorzichtig is. De twee
 * driehoeken zijn die van het vlak zelf (0-1-2 en 2-1-3), met dezelfde uv's.
 */
function maakDiepte(hoeken: { value: THREE.Vector3[] }, kaart: { value: THREE.Texture | null }) {
  const m = new THREE.MeshBasicMaterial({ colorWrite: false })
  m.onBeforeCompile = (sh) => {
    sh.uniforms.lindeHoek = hoeken
    sh.uniforms.lindeKaart = kaart
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vLindeWereld;')
      .replace('#include <project_vertex>', '#include <project_vertex>\nvLindeWereld = (modelMatrix * vec4(transformed, 1.0)).xyz;')
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>
uniform vec3 lindeHoek[4];
uniform sampler2D lindeKaart;
varying vec3 vLindeWereld;
vec3 lindeBary(vec3 p, vec3 a, vec3 b, vec3 c) {
  vec3 v0 = b - a, v1 = c - a, v2 = p - a;
  float d00 = dot(v0, v0), d01 = dot(v0, v1), d11 = dot(v1, v1), d20 = dot(v2, v0), d21 = dot(v2, v1);
  float n = d00 * d11 - d01 * d01;
  float v = (d11 * d20 - d01 * d21) / n;
  float w = (d00 * d21 - d01 * d20) / n;
  return vec3(1.0 - v - w, v, w);
}`)
      .replace('void main() {', `void main() {
  vec3 straal = vLindeWereld - cameraPosition;
  vec3 n = cross(lindeHoek[1] - lindeHoek[0], lindeHoek[2] - lindeHoek[0]);
  vec3 h = cameraPosition + straal * (dot(lindeHoek[0] - cameraPosition, n) / dot(straal, n));
  vec3 a = lindeBary(h, lindeHoek[0], lindeHoek[1], lindeHoek[2]);
  vec2 uv;
  if (min(a.x, min(a.y, a.z)) >= 0.0) {
    uv = vec2(a.y, a.z);
  } else {
    vec3 b = lindeBary(h, lindeHoek[2], lindeHoek[1], lindeHoek[3]);
    if (min(b.x, min(b.y, b.z)) < 0.0) discard;
    uv = vec2(b.y + b.z, b.x + b.z);
  }
  if (texture2D(lindeKaart, uv).a < 0.5) discard;`)
  }
  m.customProgramCacheKey = () => 'linde-diepte'
  return m
}

/** Wat een mesh had voordat hij stand-in werd. */
const ORIGINEEL = new WeakMap<THREE.Mesh, { materiaal: THREE.Material | THREE.Material[]; volgorde: number }>()

/**
 * Het model als dieptestand-in, of terug. Per frame: wat er onder de lading
 * hangt, kan wisselen (een andere pallet), en dat moet dan ook na het beeld.
 */
function zetStandIn(o: THREE.Object3D, aan: boolean, diepte: THREE.Material, lading = false) {
  const l = lading || !!o.userData.lading
  const m = o as THREE.Mesh
  if (m.isMesh) {
    if (aan && !ORIGINEEL.has(m)) ORIGINEEL.set(m, { materiaal: m.material, volgorde: m.renderOrder })
    const orig = ORIGINEEL.get(m)
    if (aan) {
      m.renderOrder = l ? 11 : 9
      if (!l) m.material = diepte
    } else if (orig) {
      m.material = orig.materiaal
      m.renderOrder = orig.volgorde
      ORIGINEEL.delete(m)
    }
  }
  for (const k of o.children) zetStandIn(k, aan, diepte, l)
}

/** Zet `renderOrder` op alles eronder, elk frame: pallets na het Linde-beeld. */
export function Volgorde({ n, children }: { n: number; children: ReactNode }) {
  const g = useRef<THREE.Group>(null)
  useFrame(() => g.current?.traverse((o) => { if ((o as THREE.Mesh).isMesh) o.renderOrder = n }))
  return <group ref={g}>{children}</group>
}

/** Of de Matics als Linde-beeld rijden: de schakelaar in de Instellingen, of `?linde` in de URL. */
export function useLindeBeeld() {
  const obj = useObjecten()
  return PROEF.linde ?? obj.lindeBeeld
}

/** Hoekverschil, -π..π. */
const verschil = (a: number, b: number) => Math.atan2(Math.sin(a - b), Math.cos(a - b))

export function LindeBeeld({ bron, drager, model, actief }: {
  bron: LindeBron
  /** De groep die met de truck meerijdt en draait: zijn stelsel is dat van het model. */
  drager: RefObject<THREE.Object3D>
  /** Het 3D-model in die groep: de dieptestand-in, en waar het beeld vóór moet liggen. */
  model: RefObject<THREE.Object3D>
  /** Uit: of het beeld dit frame getoond wordt (pas als het eerste beeld binnen is). */
  actief?: MutableRefObject<boolean>
}) {
  const geo = useMemo(() => {
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(12), 3))
    g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), 2))
    g.setAttribute('hoek', new THREE.BufferAttribute(new Float32Array([0, 1, 2, 3]), 1))
    g.setIndex([0, 1, 2, 2, 1, 3])
    return g
  }, [])
  // De hoeken als uniform en niet als vertexpositie: een uniform gaat per
  // tekenbeurt mee, een buffer pas het volgende beeld. In het stelsel van het
  // vlak, en voor de stand-in dezelfde in wereldcoördinaten.
  const hoeken = useMemo(() => ({ value: [0, 1, 2, 3].map(() => new THREE.Vector3()) }), [])
  const hoekenWereld = useMemo(() => ({ value: [0, 1, 2, 3].map(() => new THREE.Vector3()) }), [])
  /** De normaal waarmee de hal het beeld belicht (`plaats`), in het stelsel van het vlak. */
  const normaal = useMemo(() => ({ value: new THREE.Vector3(0, 1, 0) }), [])
  /** 1 in het beeld van de camera, 0 in andere tekenbeurten (de vloerspiegeling). */
  const tonen = useMemo(() => ({ value: 1 }), [])
  const kaart = useMemo(() => ({ value: null as THREE.Texture | null }), [])
  const mat = useMemo(() => {
    // Ruw en niet-metallisch: de glans zit al in het beeld van Linde; de hal
    // geeft er alleen zijn licht, kleur en schaduw over.
    const m = new THREE.MeshStandardMaterial({
      alphaTest: 0.5, side: THREE.DoubleSide, depthWrite: false, roughness: 1, metalness: 0,
      color: new THREE.Color(bron.licht, bron.licht, bron.licht),
    })
    m.onBeforeCompile = (sh) => {
      sh.uniforms.hoeken = hoeken
      sh.uniforms.lindeNormaal = normaal
      sh.uniforms.lindeTonen = tonen
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nuniform vec3 hoeken[4];\nuniform vec3 lindeNormaal;\nuniform float lindeTonen;\nattribute float hoek;')
        .replace('#include <beginnormal_vertex>', 'vec3 objectNormal = lindeNormaal;')
        .replace('#include <begin_vertex>', 'vec3 transformed = hoeken[int(hoek + 0.5)];')
        .replace('#include <project_vertex>', '#include <project_vertex>\nif (lindeTonen < 0.5) gl_Position = vec4(0.0, 0.0, 2.0, 1.0);')
    }
    m.customProgramCacheKey = () => 'linde-beeld-belicht'
    return m
  }, [bron.licht, hoeken, normaal, tonen])
  const standIn = useMemo(() => maakDiepte(hoekenWereld, kaart), [hoekenWereld, kaart])
  const hoofdCamera = useThree((s) => s.camera)

  // Beelden op aanvraag; de oudste gaan eruit.
  const cache = useRef(new Map<number, THREE.Texture>())
  const bezig = useRef(new Set<number>())
  const laad = useMemo(() => new THREE.TextureLoader(), [])
  const vraag = (f: number) => {
    const t = cache.current.get(f)
    if (t) {
      cache.current.delete(f)
      cache.current.set(f, t)
      return t
    }
    if (!bezig.current.has(f)) {
      bezig.current.add(f)
      laad.load(`${bron.map}${String(f).padStart(2, '0')}.webp`, (tex) => {
        bezig.current.delete(f)
        tex.colorSpace = THREE.SRGBColorSpace
        tex.anisotropy = 4
        cache.current.set(f, tex)
        // De oudste eruit, maar nooit het beeld dat nu te zien is.
        for (const [oud, weg] of cache.current) {
          if (cache.current.size <= VOORRAAD) break
          if (weg === mat.map) continue
          cache.current.delete(oud)
          weg.dispose()
        }
      }, undefined, () => bezig.current.delete(f))
    }
    return undefined
  }
  useEffect(() => () => {
    cache.current.forEach((t) => t.dispose())
    cache.current.clear()
    geo.dispose()
    mat.dispose()
    standIn.dispose()
  }, [geo, mat, standIn])

  const vlak = useRef<THREE.Mesh>(null)
  const aanNu = useRef(false)
  const doos = useRef<THREE.Box3 | null>(null)
  const v = useMemo(() => ({
    lokaal: new THREE.Vector3(), naar: new THREE.Vector3(), rechts: new THREE.Vector3(), boven: new THREE.Vector3(),
    midden: new THREE.Vector3(), p: new THREE.Vector3(), d: new THREE.Vector3(), voor: new THREE.Vector3(),
    hoek: new THREE.Vector3(), terug: new THREE.Matrix4(),
  }), [])

  const zet = (aan: boolean) => {
    if (vlak.current) vlak.current.visible = aan
    if (actief) actief.current = aan
    const mdl = model.current
    if (mdl && (aan || aanNu.current)) zetStandIn(mdl, aan, standIn)
    aanNu.current = aan
  }

  // Bij het weggaan het model terug zoals het was.
  useEffect(() => () => { if (model.current && aanNu.current) zetStandIn(model.current, false, standIn) }, [model, standIn])

  /**
   * Het beeld van Linde dat het dichtst bij de kijkhoek van deze camera ligt.
   *
   * In de film (`OPNAME`) vloeit hij over naar het volgende: elk deelbeeld
   * kiest het ene of het andere aanzicht, naar hoe ver de kijkhoek ertussen
   * ligt, en het gemiddelde van de zestien is een overvloei. Realtime springt
   * het beeld om de 10°; in de film loopt het over de middelste 3° in. Niet
   * breder: twee aanzichten 10° uit elkaar dekken elkaar niet, en een
   * overvloei van twee laat dunne delen dubbel zien.
   */
  const kies = (g: THREE.Object3D, camera: THREE.Camera) => {
    const c = bron.camera
    v.lokaal.copy(camera.position)
    g.worldToLocal(v.lokaal)
    const az = Math.atan2(v.lokaal.x - c.doel[0], v.lokaal.z - c.doel[2])
    const x = ((az - c.az0) * c.zin) / STAP
    let i = Math.round(x)
    if (OPNAME.aan) {
      const f = x - Math.floor(x)
      const w = THREE.MathUtils.smoothstep(f, 0.35, 0.65)
      i = Math.floor(x) + (OPNAME.deel < w ? 1 : 0)
    }
    i = ((i % 36) + 36) % 36
    let k = bron.frames.indexOf(i)
    if (k < 0) {
      k = bron.frames.reduce((best, f, j) => {
        const d = Math.abs(verschil(f * STAP, i * STAP))
        return d < Math.abs(verschil(bron.frames[best] * STAP, i * STAP)) ? j : best
      }, 0)
    }
    const f = bron.frames[k]
    const azf = c.az0 + c.zin * f * STAP
    // Hoe veel hoger deze camera op de truck neerkijkt dan die van Linde.
    const hoogte = Math.atan2(v.lokaal.y - c.doel[1], Math.hypot(v.lokaal.x - c.doel[0], v.lokaal.z - c.doel[2]))
    const hoger = hoogte - (c.hoogte + c.golf * Math.cos(azf - c.golfFase))
    return { k, f, azf, hoger }
  }

  // Per frame: aan of uit, en welke beelden er moeten komen. De plek van het
  // vlak volgt in `plaats`, na de trucks (prioriteit 0,5, vóór de composer op
  // 1): dan staat de truck waar hij dit frame staat, en weet de stand-in, die
  // vóór het beeld getekend wordt, al waar het beeld komt.
  useFrame(({ camera }) => {
    const g = drager.current
    const mdl = model.current
    if (!g || !mdl || !vlak.current) return
    if (!doos.current) {
      // De omhullende in het stelsel van de truck, één keer: wat beweegt
      // (vorken, mast) maakt voor de plek van het beeld niet uit.
      g.updateWorldMatrix(true, true)
      const w = new THREE.Box3().setFromObject(mdl)
      const b = new THREE.Box3()
      for (let h = 0; h < 8; h++) {
        v.hoek.set(h & 1 ? w.max.x : w.min.x, h & 2 ? w.max.y : w.min.y, h & 4 ? w.max.z : w.min.z)
        b.expandByPoint(g.worldToLocal(v.hoek))
      }
      doos.current = b
    }
    const { k, f } = kies(g, camera)
    vraag(f)
    vraag(bron.frames[(k + 1) % bron.frames.length])
    vraag(bron.frames[(k + bron.frames.length - 1) % bron.frames.length])
  })

  /**
   * Het vlak voor de camera neerzetten, vlak voor het tekenen — of het
   * 3D-model laten staan:
   *
   * - zolang er nog geen beeld binnen is (geen lege plek);
   * - in de film van hoog boven, met een overvloei over de deelbeelden;
   * - als de camera zo dicht bij de truck is dat een hoek van het beeldvlak
   *   naast of achter hem ligt. Een plat beeld kan dat niet: die hoek schiet
   *   dan over het hele scherm, als een uitgerekte veeg (de C-MATIC in de
   *   dwarsgang, waar de zweefroute vlak langs hem gaat).
   */
  const plaats = (camera: THREE.Camera) => {
    const g = drager.current
    const m = vlak.current
    const b = doos.current
    if (!g || !m || !b) return
    const c = bron.camera
    const { f, azf, hoger } = kies(g, camera)
    const tex = cache.current.get(f)
    if (tex && mat.map !== tex) {
      mat.map = tex
      mat.needsUpdate = true
    }
    kaart.value = mat.map
    const bovenaf = OPNAME.aan && OPNAME.bovenaf
      && OPNAME.deel2 < THREE.MathUtils.smoothstep(hoger, BOVEN.van, BOVEN.tot)
    if (!mat.map || bovenaf) {
      zet(false)
      return
    }

    // De normaal waarmee de hal het beeld belicht: half naar de camera, half
    // omhoog. Een truck krijgt zijn licht vooral van boven (de armaturen, de
    // daklichten), en wat je van hem ziet, ziet de camera.
    v.d.set(...c.doel)
    g.localToWorld(v.d)
    v.d.subVectors(camera.position, v.d).setY(0).normalize().multiplyScalar(0.6)
    v.d.addScaledVector(Y, 0.8).normalize()
    normaal.value.copy(v.d).transformDirection(v.terug.copy(m.matrixWorld).invert())

    // De camera van Linde bij dat frame, en zijn beeldvlak door de draaias.
    const e = c.hoogte + c.golf * Math.cos(azf - c.golfFase)
    v.naar.set(Math.cos(e) * Math.sin(azf), Math.sin(e), Math.cos(e) * Math.cos(azf))
    v.rechts.crossVectors(Y, v.naar).normalize()
    v.boven.crossVectors(v.naar, v.rechts)
    const H = 2 * c.afstand * Math.tan(THREE.MathUtils.degToRad(c.fov) / 2)
    v.midden.set(...c.doel)
      .addScaledVector(v.rechts, -c.schuif[0] * H)
      .addScaledVector(v.boven, c.schuif[1] * H)

    // Hoe ver het beeld naar voren moet: tot net vóór het dichtste punt.
    camera.getWorldDirection(v.voor)
    let dichtst = Infinity
    for (let h = 0; h < 8; h++) {
      v.hoek.set(h & 1 ? b.max.x : b.min.x, h & 2 ? b.max.y : b.min.y, h & 4 ? b.max.z : b.min.z)
      g.localToWorld(v.hoek)
      dichtst = Math.min(dichtst, v.d.subVectors(v.hoek, camera.position).dot(v.voor))
    }
    // Elke hoek over zijn eigen kijkstraal naar die diepte: het vlak staat dan
    // recht voor de camera, op het scherm precies waar het was.
    const diepte = Math.max(0.1, dichtst - MARGE)

    const [x0, y0, x1, y1] = bron.crop
    const N = bron.bron
    const L = (x0 / N - 0.5) * H, R = (x1 / N - 0.5) * H
    const T = (0.5 - y0 / N) * H, B = (0.5 - y1 / N) * H
    const uv: [number, number][] = [[L, B], [R, B], [L, T], [R, T]]
    for (let j = 0; j < 4; j++) {
      const [x, y] = uv[j]
      v.p.copy(v.midden).addScaledVector(v.rechts, x).addScaledVector(v.boven, y)
      g.localToWorld(v.p)
      v.d.subVectors(v.p, camera.position)
      const voor = v.d.dot(v.voor)
      // Een hoek die minder dan 30 cm voor de camera ligt: te dichtbij.
      if (voor < 0.3) {
        zet(false)
        return
      }
      v.p.copy(camera.position).addScaledVector(v.d, diepte / voor)
      hoekenWereld.value[j].copy(v.p)
      hoeken.value[j].copy(m.worldToLocal(v.p))
    }
    zet(true)
  }
  useFrame(() => plaats(hoofdCamera), 0.5)

  return (
    <mesh
      ref={vlak} geometry={geo} material={mat} frustumCulled={false} renderOrder={10} visible={false} receiveShadow
      // De vloerspiegeling tekent met een eigen camera: daar niet.
      onBeforeRender={(_r, _s, camera) => { tonen.value = camera === hoofdCamera ? 1 : 0 }}
    />
  )
}
